// Secure, in-frame viewer for the protected reference document set.
//
// All documents are served only by the authenticated /api/docs endpoint (never
// as public static files) and gated per-document by role:
//   - User Guide       any authenticated account
//   - Technical Guide  admin or NDA (module 'app_guide_nda')
//   - App Guide set     App Guide role (module 'app_guide'/'app_guide_nda', or admin)
// NDA-marked regions are stripped from the response body server-side for
// non-NDA callers. Documents render inside an in-app iframe (same origin, so
// they inherit the chosen theme) - never an external tab, never indexable.
import { getUser, getLicense } from './auth.js';

function token() { return localStorage.getItem('gdbs_token') || ''; }

function roles(user, license) {
  const u = user || getUser(), l = license || getLicense();
  const admin = u?.role === 'admin';
  const mods = l?.modules || [];
  const nda = admin || mods.includes('app_guide_nda');
  const appGuide = nda || mods.includes('app_guide');
  return { admin, nda, appGuide };
}

function show(id, on) {
  const el = document.getElementById(id);
  if (el) { if (on) el.classList.remove('hidden'); else el.classList.add('hidden'); }
}

// Reveal the role-gated menu entries. User Guide stays visible to everyone.
export function applyAppGuideAccess(user, license) {
  const r = roles(user, license);
  show('btn-app-guide', r.appGuide);
  show('btn-tech-guide', r.admin || r.nda);
}

async function authedFetch(path) {
  return fetch(path, { headers: { 'Authorization': 'Bearer ' + token() }, cache: 'no-store' });
}

let _loadedGroup = null;

function selectTab(btn, slug) {
  document.querySelectorAll('#app-guide-nav .ag-tab').forEach(t => t.classList.remove('active'));
  if (btn) btn.classList.add('active');
  loadDoc(slug);
}

async function loadManifest(group, preferSlug) {
  const nav = document.getElementById('app-guide-nav');
  if (!nav) return;
  nav.innerHTML = '<span class="ag-empty">Loading...</span>';
  try {
    const res = await authedFetch('/api/docs' + (group ? ('?group=' + encodeURIComponent(group)) : ''));
    if (!res.ok) { nav.innerHTML = '<span class="ag-empty">Access is not enabled for this account.</span>'; return; }
    const data = await res.json();
    const docs = data.docs || [];
    nav.innerHTML = '';
    if (!docs.length) { nav.innerHTML = '<span class="ag-empty">No documents available.</span>'; return; }
    docs.forEach(d => {
      const b = document.createElement('button');
      b.className = 'ag-tab';
      b.textContent = d.title;
      b.dataset.slug = d.slug;
      b.addEventListener('click', () => selectTab(b, d.slug));
      nav.appendChild(b);
    });
    if (data.nda) {
      const t = document.createElement('span');
      t.className = 'ag-nda-tag'; t.textContent = 'NDA'; t.title = 'Full (NDA) access - unredacted content';
      nav.appendChild(t);
    }
    const target = (preferSlug && docs.some(d => d.slug === preferSlug)) ? preferSlug : docs[0].slug;
    const btn = [...nav.querySelectorAll('.ag-tab')].find(b => b.dataset.slug === target);
    selectTab(btn, target);
  } catch (e) {
    nav.innerHTML = '<span class="ag-empty">Could not load the document set.</span>';
  }
}

// Intra-document anchor navigation for the srcdoc iframe. A srcdoc frame has no
// own base URL, so a bare <a href="#id"> resolves against the PARENT page and
// the click escapes to the host SPA instead of scrolling the guide. Inject a
// capturing click handler that intercepts same-page anchors and scrolls to the
// target inside the frame. Inline script is allowed by the app CSP
// (script-src 'self' 'unsafe-inline'); the frame is same-origin.
const IN_DOC_NAV =
  '<script>(function(){document.addEventListener("click",function(e){' +
  'var a=e.target&&e.target.closest&&e.target.closest("a[href^=\\"#\\"]");if(!a)return;' +
  'var id=decodeURIComponent((a.getAttribute("href")||"").slice(1));if(!id)return;' +
  'var el=document.getElementById(id);if(el){e.preventDefault();' +
  'el.scrollIntoView({behavior:"smooth",block:"start"});}},true);})();<\/script>';

function withInDocNav(html) {
  return html.includes('</body>') ? html.replace('</body>', IN_DOC_NAV + '</body>')
                                  : html + IN_DOC_NAV;
}

async function loadDoc(slug) {
  const frame = document.getElementById('app-guide-frame');
  if (!frame) return;
  frame.srcdoc = '<p style="font:15px system-ui;color:#888;padding:24px;">Loading...</p>';
  try {
    const res = await authedFetch('/api/docs/' + encodeURIComponent(slug));
    frame.srcdoc = res.ok ? withInDocNav(await res.text())
      : '<p style="font:15px system-ui;color:#888;padding:24px;">Document unavailable.</p>';
  } catch (e) {
    frame.srcdoc = '<p style="font:15px system-ui;color:#888;padding:24px;">Load error.</p>';
  }
}

function open(group, slug) {
  const modal = document.getElementById('app-guide-modal');
  if (!modal) return;
  const title = document.getElementById('app-guide-title');
  if (title) title.textContent = group === 'manual' ? 'Guides' : 'App Guide';
  modal.classList.remove('hidden');
  if (_loadedGroup !== group) {
    _loadedGroup = group;
    loadManifest(group, slug);
  } else if (slug) {
    const b = [...document.querySelectorAll('#app-guide-nav .ag-tab')].find(x => x.dataset.slug === slug);
    if (b) selectTab(b, slug);
  }
}

function close() { document.getElementById('app-guide-modal')?.classList.add('hidden'); }

export function initAppGuide() {
  document.getElementById('btn-app-guide')?.addEventListener('click', () => open('appguide'));
  document.getElementById('btn-user-guide')?.addEventListener('click', () => open('manual', 'user-guide'));
  document.getElementById('btn-tech-guide')?.addEventListener('click', () => open('manual', 'tech-guide'));
  document.getElementById('app-guide-close')?.addEventListener('click', close);
  const modal = document.getElementById('app-guide-modal');
  modal?.addEventListener('click', e => { if (e.target === modal) close(); });
  document.addEventListener('keydown', e => { if (e.key === 'Escape' && modal && !modal.classList.contains('hidden')) close(); });
  window.gdbsOpenAppGuide = open;
}
